/* eslint-disable indent */
import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { SingleValue } from "react-select";
import { toast } from "react-toastify";
import { captureException } from "@sentry/react";
import { uniqueId } from "lodash";
import Image from "next/image";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { QuestionIcon } from "components/atoms/Icon";
import { Input } from "components/atoms/Input";
import { Loader } from "components/atoms/Loader";
import { Select } from "components/atoms/Select";
import Tabs from "components/atoms/Tab";
import { Toggle } from "components/atoms/Toggle";
import { TypeaheadSelect } from "components/atoms/TypeaheadSelect";
import EnhancedModelControl from "components/molecules/EnhancedModelControl";
import * as Styled from "components/molecules/Form/Form.styled";
import { ImageViewer } from "components/molecules/ImageViewer";
import { LightboxWithMetadata } from "components/molecules/Lightbox";
import ModelSelection from "components/molecules/ModelSelection";
import { PresetSidebar } from "components/molecules/PresetSidebar";
import { PresetSubscriptionModal } from "components/molecules/PresetSubscriptionModal";
import { SavePresetModal } from "components/molecules/SavePresetModal";
import { DescriptionTooltip, PremiumTooltip } from "components/molecules/Tooltip";
import { deletePreset, editImage, generateMonsterImage, getModelConfig, listPresets, onToggleVisibility, poll, pollEditImage, pollGenerateMonsterImage, savePreset, updatePreset, voteOnImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, EditType, GroupedOption, IFreeGenModelDTO, ImageDTO, IModelConfig, IPresetDTO, IPromptOptions, Option, PresetType, RequestType, UserPremiumType } from "types";
import { calculateModelCost, calculatePremiumThemeCost, calculateSizeCost, calculateTotalGoldCost } from "utils/goldCostUtils";
import { LOADING_TEXT } from "utils/promptConstants";
import { Span } from "utils/styling/typography.styled";
type CharacterFormProps = {
  fieldOptions: {
    [key: string]: string[] | undefined;
  };
  prompts: IPromptOptions[];
};
type FormValues = {
  [key: string]: string;
};
export const GenerateMonsterForm: FC<CharacterFormProps> = ({
  fieldOptions,
  prompts
}) => {
  const [formValues, setFormValues] = useState<FormValues>({
    "Art Style": "Realistic",
    Size: "1024x1024",
    "Number of Images": "1"
  });
  const [prompt, setPrompt] = useState<IPromptOptions | undefined>();
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isPremium, setIsPremium] = useState<boolean>(false);
  const [images, setImages] = useState<ImageDTO[]>([]);
  const [selectedImage, setSelectedImage] = useState<ImageDTO | null>(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [toggles, setToggles] = useState<{
    [key: string]: boolean;
  }>(images.reduce((acc, image) => ({
    ...acc,
    [image.id]: image.isPublic
  }), {}));
  const [useCustomConfig, setUseCustomConfig] = useState(false);
  const [useModel, setUseModel] = useState(false);
  const [config, setConfig] = useState<IFreeGenModelDTO>({
    model: "",
    steps: "",
    cfg: "",
    sampler: ""
  });
  const [modelConfigs, setModelConfigs] = useState<IModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState<IModelConfig | null>(null);
  const [isSavePresetModalOpen, setIsSavePresetModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [presets, setPresets] = useState<IPresetDTO[]>([]);
  const [goldCost, setGoldCost] = useState(0);
  const standardSizes = ["1024x1024", "896x1152", "832x1216", "768x1344", "640x1536", "1536x640", "1344x768", "1216x832", "1152x896"];
  const premiumSizes = ["1024x1024", "1024x1792", "1792x1024"];
  const [manualPrompt, setManualPrompt] = useState(false);
  useEffect(() => {
    const totalCost = calculateTotalGoldCost(selectedModel?.modelName || null, isPremium, formValues["Size"] || null, prompt?.premium || false, useCustomConfig ? parseInt(config.steps || "0", 10) : 0, isPremium ? 1 : parseInt(formValues["Number of Images"]) || 1);
    setGoldCost(totalCost);
  }, [selectedModel, isPremium, formValues, config, prompt, useCustomConfig]);
  useEffect(() => {
    if (selectedModel?.modelName === "Flux.Pro" || isPremium) {
      setFormValues(prevFormValues => ({
        ...prevFormValues,
        "Number of Images": "1"
      }));
    }
  }, [selectedModel, isPremium]);
  const handleSavePreset = () => {
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      setIsSubscriptionModalOpen(true);
    } else {
      setIsSavePresetModalOpen(true);
    }
  };
  const handleDeletePreset = async (presetId: string) => {
    await deletePreset(presetId);
    setPresets(prevPresets => prevPresets.filter(preset => preset.id !== presetId));
    toast.success("Preset deleted successfully!", {
      position: "bottom-center"
    });
  };
  const handleSave = async (presetName: string) => {
    try {
      const newPresetDTO: IPresetDTO = {
        id: uniqueId(),
        name: presetName,
        prompt: prompt?.shortName || "",
        artStyle: formValues["Art Style"] || "",
        theme: formValues["Theme"] || "",
        requestFields: formValues,
        presetType: PresetType.MONSTER,
        additionalField: formValues["Description"] || "",
        freeGenModelDTO: (useCustomConfig || useModel) && !isPremium ? config : undefined,
        premium: isPremium,
        isCustomConfig: useCustomConfig
      };
      const existingPreset: IPresetDTO | undefined = presets.find(preset => preset.name === presetName);
      let response;
      if (existingPreset) {
        // Update existing preset
        newPresetDTO.id = existingPreset.id;
        response = await updatePreset(existingPreset.id, newPresetDTO);
      } else {
        // Create new preset
        response = await savePreset(newPresetDTO);
      }
      if (response && (response as IPresetDTO).id) {
        const updatedPreset = response as IPresetDTO;
        setPresets(prevPresets => {
          const filteredPresets = prevPresets.filter(preset => preset.id !== updatedPreset.id);
          return [...filteredPresets, updatedPreset];
        });
        toast.success("Preset saved successfully!", {
          position: "bottom-center"
        });
        setIsSavePresetModalOpen(false);
      } else {
        console.error("Unexpected response:", response);
      }
    } catch (error) {
      console.error("Error saving preset:", error);
    }
  };
  const handleApplyPreset = (preset: IPresetDTO) => {
    setFormValues(preset.requestFields);
    setPrompt(findTheme(preset.prompt));
    setIsPremium(preset.premium);
    setConfig(preset.freeGenModelDTO || {
      model: "",
      steps: "",
      cfg: "",
      sampler: ""
    });
    const conf = preset.freeGenModelDTO ? true : false;
    setUseCustomConfig(preset.isCustomConfig);
    setUseModel(conf);
    const selectedPresetModel = modelConfigs.find(model => model.modelName === preset.freeGenModelDTO?.model);
    if (selectedPresetModel) {
      setSelectedModel(selectedPresetModel); // Update selected model
    }
    toast.success(`Preset "${preset.name}" applied successfully!`, {
      position: "bottom-center"
    });
  };
  useEffect(() => {
    const fetchPresets = async () => {
      try {
        const response = await listPresets(PresetType.MONSTER);
        if (Array.isArray(response)) {
          setPresets(response);
        } else {
          console.error("Error fetching presets:", response.error);
        }
      } catch (error) {
        console.error("Error fetching presets:", error);
      }
    };
    fetchPresets();
  }, []);
  const handleConfigChange = (newConfig: Partial<IFreeGenModelDTO>) => {
    setUseModel(true);
    setConfig(prevConfig => ({
      ...prevConfig,
      ...newConfig
    }));
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getModelConfig();
        setModelConfigs(data.modelConfigs);
        setUseModel(true);
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, []);
  const handleIndexChange = (newIndex: number) => {
    setSelectedImage(images[newIndex]);
  };
  useEffect(() => {
    setError("");
  }, [prompt]);
  useEffect(() => {
    if (prompts.length > 0) {
      setPrompt(prompts[0]);
    }
  }, [prompts]);
  const updateImage = (updatedImage: ImageDTO) => {
    setImages(prevImages => {
      return prevImages.map(image => image.id === updatedImage.id ? updatedImage : image);
    });
  };
  const handleDescriptionChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = event.target;
    setFormValues(prevState => ({
      ...prevState,
      Description: value
    }));
  }, []);
  useEffect(() => {
    if (state.gold === 0) {
      setIsPremium(false);
      setPrompt(prompts[0]);
      handleDescriptionChange({
        target: {
          value: ""
        }
      } as React.ChangeEvent<HTMLInputElement>);
      const selectedPresetModel = modelConfigs.find(model => model.modelName === "sdxl");
      if (selectedPresetModel) {
        setSelectedModel(selectedPresetModel); // Update selected model
      }
    }
  }, [handleDescriptionChange, prompts, state.gold, modelConfigs]);
  const handlePremiumToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (prompt?.premium && state.userPremiumLevel === UserPremiumType.NONE) {
      return;
    }
    setIsPremium(checked);

    // If the toggle is being turned off and the user's premium level is NONE, reset the description
    if (!checked && state.userPremiumLevel === UserPremiumType.NONE) {
      handleDescriptionChange({
        target: {
          value: ""
        }
      } as React.ChangeEvent<HTMLInputElement>);
    }
  }, [handleDescriptionChange, prompt?.premium, state.userPremiumLevel]);
  const findTheme = useMemo(() => {
    return (p: string) => prompts.find(prompt => prompt.shortName === p);
  }, [prompts]);
  const handleThemeChange = (selectedOption: SingleValue<Option>) => {
    if (selectedOption?.isDisabled) {
      return;
    }
    const selectedPrompt = findTheme(selectedOption?.value ?? "");
    if (!selectedPrompt) {
      setPrompt({
        shortName: selectedOption?.value as string,
        fullName: selectedOption?.value as string,
        premium: false
      });
    } else {
      setPrompt(selectedPrompt);
    }
    if (selectedPrompt?.premium && state.userPremiumLevel === UserPremiumType.NONE) {
      setIsPremium(true);
    }
  };
  const groupedOptions: GroupedOption[] = useMemo(() => [{
    label: "Free Themes",
    options: prompts.filter(({
      premium
    }) => !premium).map(({
      shortName,
      fullName
    }) => ({
      value: shortName,
      label: fullName
    }))
  }, {
    label: "Premium Themes",
    options: prompts.filter(({
      premium
    }) => premium).map(({
      shortName,
      fullName
    }) => ({
      value: shortName,
      label: fullName,
      isDisabled: state.gold === 0 && state.userPremiumLevel === UserPremiumType.NONE
    }))
  }], [prompts, state.gold, state.userPremiumLevel]);
  const handleAppearanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = event.target;
    setFormValues(prevState => ({
      ...prevState,
      Appearance: value
    }));
  };
  const handleInputThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = event.target;
    setPrompt({
      shortName: value,
      fullName: value,
      premium: false
    });
  };
  const handleSelectChange = (field: string) => (selectedOption: {
    value: string;
    label: string;
  } | null) => {
    setFormValues(prev => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : ""
    }));
  };
  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormValues(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  const handleSetImages = (response: any) => {
    try {
      if (Array.isArray(response)) {
        setImages(response);
      } else {
        setImages([response]);
      }
    } catch (error) {
      console.error("Error parsing response:", error);
    }
  };
  const handleImageClick = (image: ImageDTO) => {
    setSelectedImage(image);
    setIsLightboxOpen(true);
  };
  const handleGenerateNpcImage = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    if (!state.isLoggedIn) {
      e.preventDefault();
      e.stopPropagation();
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    setError("");
    e.preventDefault();
    if (state.userPremiumLevel === UserPremiumType.NONE && state.gold < goldCost) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    } else if (state.gold < goldCost) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const filteredFormValues: FormValues = manualPrompt ? Object.keys(formValues).filter((key): key is "Number of Images" | "Size" => key === "Number of Images" || key === "Size").reduce((obj, key) => {
      obj[key] = formValues[key];
      return obj;
    }, {} as FormValues) : formValues;
    const dataPayload = {
      prompt: prompt ? prompt.shortName : undefined,
      requestFields: filteredFormValues,
      requestType: RequestType.MONSTER,
      manualPrompt: manualPrompt,
      ...(config && !isPremium && (useCustomConfig || useModel) && {
        model: config
      })
    };
    setLoading(true);
    const {
      requestId,
      error
    } = await generateMonsterImage(dataPayload, isPremium && !!state.gold);
    setError(error || "");
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => await pollGenerateMonsterImage(requestId, isPremium && !!state.gold), ({
        image
      }) => !image, 5000);
      setError(error || "");
      handleSetImages(image || "");
      if (image) {
        dispatch({
          type: AppReducerActionType.SET_GOLD,
          payload: {
            ...state,
            gold: state.gold - goldCost
          }
        });
      }
    }
    setLoading(false);
  }, [config, dispatch, formValues, isPremium, prompt, state, useCustomConfig, useModel, goldCost, manualPrompt]);
  const handleLightboxClose = () => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
    dispatch({
      type: AppReducerActionType.TOGGLE_OVERLAY_VISIBILITY,
      payload: {
        ...state,
        isOverlayVisible: false
      }
    });
  };
  const handleToggle = (imageId: string) => {
    const currentVisibility = toggles[imageId];
    const newVisibility = !currentVisibility;
    onToggleVisibility(imageId, newVisibility).then(() => {
      setToggles(prev => ({
        ...prev,
        [imageId]: newVisibility
      }));
      setImages(prev => prev.map(image => image.id === imageId ? {
        ...image,
        isPublic: newVisibility
      } : image));
    }).catch(error => {
      captureException("Failed to update visibility status.", error);
    });
  };
  const handleVote = async (imageId: string) => {
    const currentImage = images.find(img => img.id === imageId);
    if (!currentImage) return;
    const newVoteStatus = !currentImage.isVotedByUser;
    try {
      await voteOnImage(imageId, newVoteStatus);
      const newImageDtos = images.map(img => img.id === imageId ? {
        ...img,
        isVotedByUser: newVoteStatus,
        voteCount: img.voteCount + (newVoteStatus ? 1 : -1)
      } : img);
      setImages(newImageDtos);
      if (selectedImage?.id === imageId) {
        setSelectedImage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            isVotedByUser: newVoteStatus,
            voteCount: prev.voteCount + (newVoteStatus ? 1 : -1)
          };
        });
      }
    } catch (error: any) {
      captureException("Failed to update vote status.", error);
    }
  };
  const handleEditSubmit = useCallback(async (editRequest: string, isPremium: boolean, imageId: string, editType: string, negativePrompt?: string, cfgScale?: number, numDetections?: number, urlOrFile?: string | File, base64Image?: string) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    setError("");
    if (!isPremium && state.userPremiumLevel === UserPremiumType.NONE && state.userFreeGens === 0) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("editRequest", editRequest);
    formData.append("imageId", imageId);
    formData.append("premium", isPremium.toString());
    formData.append("editType", editType);
    if (negativePrompt) {
      formData.append("negativePrompt", negativePrompt);
    }
    if (cfgScale) {
      formData.append("cfgScale", cfgScale.toString());
    }
    if (numDetections) {
      formData.append("numDetections", numDetections.toString());
    }
    if (urlOrFile) {
      if (typeof urlOrFile === "string") {
        formData.append("faceImageUrl", urlOrFile);
      } else {
        formData.append("faceImageFile", urlOrFile);
      }
    }
    if (base64Image) {
      formData.append("base64Image", base64Image);
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    setError(error || "");
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => await pollEditImage(requestId), ({
        image
      }) => !image || image.length === 0, 5000);
      setError(error || "");
      let imagesToSet: ImageDTO[] = [];

      // Ensure `images` is an array
      if (Array.isArray(image)) {
        image.forEach((element: ImageDTO) => {
          if (element.parentImage) {
            imagesToSet.push(element.parentImage);
          }
          imagesToSet.push(element);
        });
      } else if (image) {
        if (image.parentImage) {
          imagesToSet.push(image.parentImage);
        }
        imagesToSet.push(image);
      }
      handleSetImages(imagesToSet);
      if (image && image.length > 0) {
        const goldCost = editType === EditType.FACESWAP ? 4 : 1;
        dispatch({
          type: AppReducerActionType.SET_GOLD,
          payload: {
            ...state,
            gold: state.gold - goldCost
          }
        });
      }
    }
    setLoading(false);
    setIsLightboxOpen(false); // Close the lightbox modal
  }, [dispatch, state]);
  const [isFreeText, setIsFreeText] = useState<{
    [key: string]: boolean;
  }>({});
  const toggleFreeText = (field: string) => {
    setIsFreeText(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  const renderDetailedForm = () => <Styled.TabsWrapper data-sentry-element="unknown" data-sentry-component="renderDetailedForm" data-sentry-source-file="index.tsx">
            {Object.entries(fieldOptions).map(([fieldName, options], index) => options ?
    // Ensure options are defined
    <Styled.FormGroup key={index}>
                        <Styled.LabelWrapper>
                            <Styled.Label htmlFor={fieldName}>
                                {fieldName}
                            </Styled.Label>
                            <Styled.InlineButton onClick={() => toggleFreeText(fieldName)}>
                                {isFreeText[fieldName] ? "Select from List" : "Enter Free Text"}
                            </Styled.InlineButton>
                        </Styled.LabelWrapper>
                        {isFreeText[fieldName] ? <Input id={fieldName} name={fieldName} value={formValues[fieldName]} onChange={handleInputChange(fieldName)} placeholder={`Enter ${fieldName}`} multiline /> : <TypeaheadSelect id={fieldName} name={fieldName} value={formValues[fieldName] ? {
        value: formValues[fieldName],
        label: formValues[fieldName]
      } : null} onChange={handleSelectChange(fieldName)} options={options.map(option => ({
        value: option,
        label: option
      }))} placeholder={`Select ${fieldName}`} isClearable={fieldName !== "Art Style"} />}
                    </Styled.FormGroup> : null)}
            <Styled.FormGroup key="prompt" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.LabelWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Label htmlFor="prompt" data-sentry-element="unknown" data-sentry-source-file="index.tsx">Theme:</Styled.Label>
                    {!isPremium && prompt?.premium && <Styled.GoldCost>{`Cost: ${calculatePremiumThemeCost(isPremium, prompt?.premium)} gold`}</Styled.GoldCost>}
                    <Styled.InlineButton onClick={() => toggleFreeText("prompt")} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {isFreeText["prompt"] ? "Select from List" : "Enter Free Text"}
                    </Styled.InlineButton>
                </Styled.LabelWrapper>
                {isFreeText["prompt"] ? <Input id={"prompt"} name={"prompt"} value={prompt?.fullName || ""} onChange={handleInputThemeChange} placeholder={"Enter theme"} multiline /> : <TypeaheadSelect name="prompt" id="prompt" placeholder="Type" required aria-required value={prompt ? {
        value: prompt.shortName,
        label: prompt.fullName
      } : null} onChange={handleThemeChange} options={groupedOptions} isDisabled={loading} />}
            </Styled.FormGroup>
            <Styled.FormGroup $fullWidth={true} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Label htmlFor="Appearance" data-sentry-element="unknown" data-sentry-source-file="index.tsx">Appearance:</Styled.Label>
                <Input id="Appearance" name="Appearance" onChange={handleAppearanceChange} disabled={loading} value={formValues["Appearance"] || ""} multiline data-sentry-element="Input" data-sentry-source-file="index.tsx" />
            </Styled.FormGroup>
            <Styled.FormGroup $fullWidth={true} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Label htmlFor="Description" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    Additional Information:
                    <Styled.Icon id="descriptionIcon" aria-label="Description is only available for Subscribers or using Premium Mode" onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Image height={24} width={24} priority src={QuestionIcon} alt="Tooltip" data-tooltip-id="descriptionTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                        <DescriptionTooltip data-sentry-element="DescriptionTooltip" data-sentry-source-file="index.tsx" />
                    </Styled.Icon>
                </Styled.Label>
                <Input id="Description" name="Description" onChange={handleDescriptionChange} disabled={loading || !isPremium && state.userPremiumLevel === UserPremiumType.NONE} value={formValues["Description"] || ""} multiline data-sentry-element="Input" data-sentry-source-file="index.tsx" />
            </Styled.FormGroup>
        </Styled.TabsWrapper>;
  const renderSimpleForm = () => <Styled.FormGroup key="simple-prompt" $fullWidth={true} data-sentry-element="unknown" data-sentry-component="renderSimpleForm" data-sentry-source-file="index.tsx">
            <Styled.LabelWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.Label htmlFor="simplePrompt" data-sentry-element="unknown" data-sentry-source-file="index.tsx">Prompt:</Styled.Label>
                {!isPremium && prompt?.premium && <Styled.GoldCost>{`Cost: ${calculatePremiumThemeCost(isPremium, prompt?.premium)} gold`}</Styled.GoldCost>}
            </Styled.LabelWrapper>
            <Input id={"simplePrompt"} name={"simplePrompt"} value={prompt?.fullName || ""} onChange={handleInputThemeChange} placeholder={"Enter your prompt"} multiline data-sentry-element="Input" data-sentry-source-file="index.tsx" />
        </Styled.FormGroup>;
  const handleTabChange = (index: number) => {
    if (index === 1) {
      setManualPrompt(true);
      setPrompt({
        shortName: "",
        fullName: "",
        premium: false
      });
    } else {
      setManualPrompt(false);
      setPrompt(prompts[0]);
    }
  };
  const tabs = [{
    label: "Detailed Form",
    content: renderDetailedForm()
  }, {
    label: "Manual Prompt",
    content: renderSimpleForm()
  }];
  return <>
            <Styled.Title data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                DnD Monster Generator. AI Generated Images for your D&D
                Monsters.
            </Styled.Title>
            <Styled.UploadForm data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                {state.isLoggedIn && <PresetSidebar presets={presets} onClick={handleApplyPreset} onDelete={handleDeletePreset} />}
                <Styled.FormContainer $hasImages={loading || images.length > 0} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Form onSubmit={handleGenerateNpcImage} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        {!isPremium && <Styled.FormGroup>
                                <ModelSelection modelConfigs={modelConfigs} onConfigChange={handleConfigChange} selectedModel={selectedModel} setSelectedModel={setSelectedModel} />
                                <Styled.GoldCost>{`Cost: ${calculateModelCost(selectedModel?.modelName)} gold`}</Styled.GoldCost>
                            </Styled.FormGroup>}
                        {!isPremium && <Styled.FormGroup>
                                <EnhancedModelControl config={config} onConfigChange={handleConfigChange} useCustomConfig={useCustomConfig} setUseCustomConfig={setUseCustomConfig} />
                            </Styled.FormGroup>}
                        <Styled.FormGroup data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.LabelWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.Label htmlFor="Size" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Image Size (WxH):
                                </Styled.Label>

                                {isPremium && formValues["Size"] !== "1024x1024" && <Styled.GoldCost>{`Cost: x${calculateSizeCost(formValues["Size"] || null, isPremium)}`}</Styled.GoldCost>}
                            </Styled.LabelWrapper>
                            <Select id="Size" name="Size" onChange={handleInputChange("Size")} disabled={loading} value={formValues["Size"] || ""} data-sentry-element="Select" data-sentry-source-file="index.tsx">
                                {(isPremium ? premiumSizes : standardSizes).map(size => <option key={size} value={size}>
                                            {size}
                                        </option>)}
                            </Select>
                        </Styled.FormGroup>
                        <Styled.FormGroup data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.LabelWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                <Styled.Label htmlFor="Num Images" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    Number of Images:
                                </Styled.Label>

                                {!isPremium && <Styled.GoldCost>
                                        Cost: x
                                        {formValues["Number of Images"] || "1"}
                                    </Styled.GoldCost>}
                            </Styled.LabelWrapper>

                            {isPremium || selectedModel?.modelName === "Flux.Pro" ? <Input id="Num Images" name="Num Images" value="1" disabled /> : <Input id="Num Images" name="Num Images" type="number" min="1" max="4" onChange={handleInputChange("Number of Images")} disabled={loading} value={formValues["Number of Images"] || "1"} />}
                        </Styled.FormGroup>
                        <Styled.TabsContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Tabs tabs={tabs} onTabChange={handleTabChange} data-sentry-element="Tabs" data-sentry-source-file="index.tsx" />
                        </Styled.TabsContainer>
                        <Styled.FormGroup $fullWidth={true} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Styled.Label htmlFor="premiumToggle" data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                Premium Content:
                                <Styled.Icon aria-label="Learn more about Premium Content" onClick={e => {
                e.preventDefault();
                e.stopPropagation();
              }} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                                    <Image height={24} width={24} priority src={QuestionIcon} alt="Tooltip" data-tooltip-id="premiumTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                                    <PremiumTooltip data-sentry-element="PremiumTooltip" data-sentry-source-file="index.tsx" />
                                </Styled.Icon>
                            </Styled.Label>
                            <Toggle id="premiumToggle" checked={isPremium && !!state.gold} onChange={handlePremiumToggle} disabled={!state.gold} data-sentry-element="Toggle" data-sentry-source-file="index.tsx" />
                        </Styled.FormGroup>
                        <Button type="submit" variant={isPremium && !!state.gold ? ButtonType.SUCCESS : ButtonType.PRIMARY} disabled={loading} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                            <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">
                                Generate{" "}
                                {isPremium && !!state.gold && "Premium"} Image
                            </Span>
                        </Button>
                        <Styled.Button type="button" onClick={handleSavePreset} variant={ButtonType.PRIMARY} disabled={loading} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Save as Preset</Span>
                        </Styled.Button>
                        <Styled.TotalGoldCost data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            Total Cost: {goldCost} gold
                        </Styled.TotalGoldCost>
                    </Styled.Form>
                </Styled.FormContainer>
                {error && <p>{error}</p>}
                <Styled.ImageViewerContainer $hasImages={loading || images.length > 0} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {loading && <Loader prompts={LOADING_TEXT} />}
                    <ImageViewer imageDtos={images} onImageClick={handleImageClick} updateImage={updateImage} onSubmitEdit={handleEditSubmit} onVote={handleVote} data-sentry-element="ImageViewer" data-sentry-source-file="index.tsx" />
                </Styled.ImageViewerContainer>
                <LightboxWithMetadata onClose={handleLightboxClose} onToggleVisibility={handleToggle} onVote={handleVote} imageDtos={images} initialImageId={selectedImage?.id || ""} open={isLightboxOpen} onIndexChange={handleIndexChange} data-sentry-element="LightboxWithMetadata" data-sentry-source-file="index.tsx" />
                <PresetSubscriptionModal isOpen={isSubscriptionModalOpen} onCancel={() => setIsSubscriptionModalOpen(false)} data-sentry-element="PresetSubscriptionModal" data-sentry-source-file="index.tsx" />
                <SavePresetModal isOpen={isSavePresetModalOpen} onCancel={() => setIsSavePresetModalOpen(false)} presets={presets} onSave={handleSave} data-sentry-element="SavePresetModal" data-sentry-source-file="index.tsx" />
            </Styled.UploadForm>
        </>;
};